import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, Shield, ShieldOff, X, Eye, EyeOff } from "lucide-react";
import { listUsers, createUser, updateUser, deleteUser } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin — Full access", desc: "Create, edit, delete everything including users." },
  { value: "user", label: "User — Editor", desc: "Can create and edit content, but cannot delete anything." },
];

const inputCls =
  "w-full bg-transparent border-b border-border rounded-none px-0 py-2.5 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors duration-300 font-sans text-sm";

export default function AdminUsers() {
  const { admin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listUsers());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (u) => {
    if (u.email === admin?.email) {
      toast.error("You cannot delete your own account.");
      return;
    }
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await deleteUser(u.id);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleActive = async (u) => {
    try {
      const updated = await updateUser(u.id, { is_active: !u.is_active });
      setUsers((list) => list.map((x) => (x.id === u.id ? updated : x)));
      toast.success(updated.is_active ? "User activated" : "User deactivated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div data-testid="page-admin-users" className="mx-auto max-w-[1400px] px-6 lg:px-12 py-12 lg:py-20">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <div className="overline">User Administration</div>
          <h1 className="mt-4 serif text-4xl md:text-5xl font-light leading-tight text-foreground">
            Team access.
          </h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Admins have full control. Users can edit content but cannot delete anything or manage other users.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-users-add"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus size={13} strokeWidth={1.5} />
          Add user
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center italic serif text-xl text-muted-foreground">
          <span className="ornament">Loading</span>
        </div>
      ) : users.length === 0 ? (
        <div className="py-20 border border-dashed border-border text-center text-muted-foreground italic">
          No users yet.
        </div>
      ) : (
        <div className="border border-border">
          <div className="grid grid-cols-[1fr_120px_110px_auto] items-center gap-4 px-5 py-3 border-b border-border bg-card/40 text-[0.62rem] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
            <div>Email</div>
            <div>Role</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>
          {users.map((u) => {
            const isSelf = u.email === admin?.email;
            return (
              <div
                key={u.id}
                data-testid={`admin-user-row-${u.email}`}
                className="grid grid-cols-[1fr_120px_110px_auto] items-center gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-card/40 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm text-foreground truncate">{u.email}</div>
                  {isSelf && (
                    <div className="text-[0.6rem] uppercase tracking-[0.18em] text-primary/80 mt-0.5">
                      You
                    </div>
                  )}
                </div>
                <div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-1 text-[0.62rem] uppercase tracking-[0.18em] font-semibold border ${
                      u.role === "admin"
                        ? "border-primary/60 text-primary bg-primary/5"
                        : "border-border text-foreground/80"
                    }`}
                  >
                    {u.role === "admin" ? <Shield size={10} strokeWidth={1.5} /> : <ShieldOff size={10} strokeWidth={1.5} />}
                    {u.role}
                  </span>
                </div>
                <div>
                  <button
                    type="button"
                    data-testid={`admin-user-toggle-active-${u.email}`}
                    onClick={() => toggleActive(u)}
                    disabled={isSelf}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 text-[0.62rem] uppercase tracking-[0.18em] font-semibold border transition-colors ${
                      u.is_active
                        ? "border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground"
                        : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    title={isSelf ? "You cannot deactivate yourself" : (u.is_active ? "Deactivate" : "Activate")}
                  >
                    {u.is_active ? <Eye size={10} strokeWidth={1.5} /> : <EyeOff size={10} strokeWidth={1.5} />}
                    {u.is_active ? "Active" : "Inactive"}
                  </button>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    data-testid={`admin-user-edit-${u.email}`}
                    onClick={() => { setEditing(u); setShowForm(true); }}
                    className="px-3 py-1.5 border border-border text-[0.62rem] uppercase tracking-[0.18em] text-foreground/85 hover:border-primary hover:text-primary transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-user-delete-${u.email}`}
                    onClick={() => handleDelete(u)}
                    disabled={isSelf}
                    className="p-1.5 border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title={isSelf ? "You cannot delete yourself" : "Delete"}
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <UserFormModal
          user={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function UserFormModal({ user, onClose, onSaved }) {
  const isEdit = !!user;
  const [email, setEmail] = useState(user?.email || "");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(user?.role || "user");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!isEdit && (!email || !password)) {
      toast.error("Email and password are required.");
      return;
    }
    if (password && password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { role };
        if (password) payload.password = password;
        await updateUser(user.id, payload);
        toast.success("User updated");
      } else {
        await createUser({ email, password, role });
        toast.success("User created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 backdrop-blur-lg p-4"
      onClick={onClose}
      data-testid="admin-user-form"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md bg-card border border-border p-8 space-y-6"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="overline">{isEdit ? "Edit user" : "New user"}</div>
            <div className="mt-2 serif text-2xl font-light leading-tight">
              {isEdit ? user.email : "Grant access."}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <X size={13} strokeWidth={1.5} />
          </button>
        </div>

        {!isEdit && (
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              autoFocus
              required
              data-testid="admin-user-form-email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="editor@nelumdining.com"
            />
          </div>
        )}

        <div>
          <label className="overline block mb-2">
            {isEdit ? "New password (optional)" : "Password"}
          </label>
          <input
            type="password"
            required={!isEdit}
            data-testid="admin-user-form-password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isEdit ? "Leave blank to keep current" : "Min 8 characters"}
            minLength={8}
          />
        </div>

        <div>
          <label className="overline block mb-3">Role</label>
          <div className="space-y-2">
            {ROLE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                data-testid={`admin-user-form-role-${opt.value}`}
                className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                  role === opt.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-foreground/40"
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value={opt.value}
                  checked={role === opt.value}
                  onChange={(e) => setRole(e.target.value)}
                  className="mt-1 accent-primary"
                />
                <div>
                  <div className="text-sm text-foreground font-medium">{opt.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] text-foreground/70 hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-user-form-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-[0.7rem] uppercase tracking-[0.22em] font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <Save size={11} strokeWidth={1.5} />
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create user")}
          </button>
        </div>
      </form>
    </div>
  );
}
